import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Plus, Pencil, Trash2, Calendar, Check, Target, X, Shield, Crown, AlertTriangle, Clock, Trophy, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stages = ['pool_a', 'pool_b', 'pool_c', 'pool_d', 'quarterfinal', 'semifinal', 'final', 'group'];

const stageLabels = {
  'pool_a': 'POOL A',
  'pool_b': 'POOL B', 
  'pool_c': 'POOL C',
  'pool_d': 'POOL D',
  'quarterfinal': 'QUARTER-FINALS',
  'semifinal': 'SEMI-FINALS',
  'gala': 'GALA MATCH - ABOVE 45 YEARS',
  'final': 'FINAL',
  'group': 'GROUP STAGE'
};

export default function MatchesPage() {
  const { token, organization } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [formData, setFormData] = useState({
    home_team_id: '',
    away_team_id: '',
    match_date: '',
    match_time: '',
    stage: 'group'
  });
  const [scoreData, setScoreData] = useState({
    home_score: 0,
    away_score: 0,
    field: '',
    match_time: '',
    match_date: ''
  });
  const [goalScorers, setGoalScorers] = useState([]);
  const [matchEvents, setMatchEvents] = useState([]);
  const [penalties, setPenalties] = useState(false);
  const [penScore, setPenScore] = useState({ home: 0, away: 0 });
  
  // Referee assignment states
  const [refereeDialogOpen, setRefereeDialogOpen] = useState(false);
  const [referees, setReferees] = useState([]);
  const [selectedMatchForReferee, setSelectedMatchForReferee] = useState(null);
  const [refereeAssignment, setRefereeAssignment] = useState({
    main_referee_id: '',
    assistant_referee_1_id: '',
    assistant_referee_2_id: '',
    fourth_official_id: '',
    match_delegate_id: ''
  });
  
  const canManageReferees = organization?.plan === 'pro' || organization?.plan === 'enterprise';
  const [autoPopulating, setAutoPopulating] = useState(false);

  const handleAutoPopulate = async () => {
    if (!confirm('This will create 12 teams in 4 pools (A-D) and generate all group stage + knockout matches. Continue?')) return;
    setAutoPopulating(true);
    try {
      const response = await fetch(`${API_URL}/api/tournament/auto-populate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Tournament created! ${data.teams_created} teams, ${data.matches_created} matches`);
        fetchTeams();
        fetchMatches();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to auto-populate');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setAutoPopulating(false);
    }
  };

  useEffect(() => {
    if (organization) {
      fetchTeams();
      fetchMatches();
      fetchPlayers();
      if (canManageReferees) {
        fetchReferees();
      }
    }
  }, [organization]);

  const fetchPlayers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/players/${organization.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPlayers(data);
      }
    } catch (error) {
      console.error('Failed to fetch players:', error);
    }
  };

  const fetchReferees = async () => {
    try {
      const response = await fetch(`${API_URL}/api/referees/${organization.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReferees(data.filter(r => r.is_active));
      }
    } catch (error) {
      console.error('Failed to fetch referees:', error);
    }
  };

  const openRefereeDialog = (match) => {
    setSelectedMatchForReferee(match);
    setRefereeAssignment({
      main_referee_id: match.main_referee_id || '',
      assistant_referee_1_id: match.assistant_referee_1_id || '',
      assistant_referee_2_id: match.assistant_referee_2_id || '',
      fourth_official_id: match.fourth_official_id || '',
      match_delegate_id: match.match_delegate_id || ''
    });
    setRefereeDialogOpen(true);
  };

  const handleRefereeAssignment = async () => {
    if (!selectedMatchForReferee) return;
    
    try {
      // Clean — convert empty strings to null
      const cleanAssignment = {};
      Object.entries(refereeAssignment).forEach(([key, val]) => {
        cleanAssignment[key] = val || null;
      });
      
      const response = await fetch(`${API_URL}/api/matches/${selectedMatchForReferee.match_id}/assign-referees`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(cleanAssignment)
      });
      
      if (response.ok) {
        toast.success('Referees assigned successfully');
        fetchMatches();
        setRefereeDialogOpen(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign referees');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const getRefereeNameById = (refId) => {
    if (!refId) return '';
    const referee = referees.find(r => r.referee_id === refId);
    return referee?.name || '';
  };

  // Only show referee if the ID resolves to a real name
  const hasValidReferee = (match) => {
    return match.main_referee_id && getRefereeNameById(match.main_referee_id);
  };

  const fetchTeams = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/${organization.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data);
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await fetch(`${API_URL}/api/matches/${organization.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMatches(data);
      }
    } catch (error) {
      console.error('Failed to fetch matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.home_team_id === formData.away_team_id) {
      toast.error('Home and away teams must be different');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/matches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          org_id: organization.org_id
        })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create match');
      }
      toast.success('Match scheduled!');
      setDialogOpen(false);
      setFormData({
        home_team_id: '',
        away_team_id: '',
        match_date: '',
        match_time: '',
        stage: 'group'
      });
      fetchMatches();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateScore = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/matches/${editingMatch.match_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          home_score: parseInt(scoreData.home_score),
          away_score: parseInt(scoreData.away_score),
          goal_scorers: goalScorers.filter(gs => gs.player_id),
          match_events: matchEvents.filter(e => e.event_type),
          field: scoreData.field || null,
          match_time: scoreData.match_time || null,
          match_date: scoreData.match_date || null,
          penalties: penalties || false,
          home_pen_score: penalties ? penScore.home : null,
          away_pen_score: penalties ? penScore.away : null,
          status: 'completed'
        })
      });
      if (!response.ok) throw new Error('Failed to update score');
      toast.success('Score updated!');
      setScoreDialogOpen(false);
      setEditingMatch(null);
      setGoalScorers([]);
      setMatchEvents([]);
      fetchMatches();
    } catch (error) {
      toast.error(String(error.message || 'Failed to update score'));
    }
  };

  const handleDelete = async (matchId) => {
    if (!confirm('Are you sure you want to delete this match?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/matches/${matchId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete match');
      toast.success('Match deleted!');
      fetchMatches();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openScoreDialog = (match) => {
    setEditingMatch(match);
    setScoreData({
      home_score: match.home_score || 0,
      away_score: match.away_score || 0,
      field: match.field || '',
      match_time: match.match_time || '',
      match_date: match.match_date || ''
    });
    setGoalScorers(match.goal_scorers || []);
    // Clean up generic "Goal" player_name so dropdown shows "Select player" prompt
    const cleanedEvents = (match.match_events || []).map(evt => {
      if (!evt.player_id && evt.player_name === 'Goal') {
        return { ...evt, player_name: '' };
      }
      return evt;
    });
    setMatchEvents(cleanedEvents);
    setPenalties(match.penalties || false);
    setPenScore({ home: match.home_pen_score || 0, away: match.away_pen_score || 0 });
    setScoreDialogOpen(true);
  };

  const addGoalScorer = () => {
    setGoalScorers([...goalScorers, { player_id: '', goals: 1 }]);
  };

  const removeGoalScorer = (index) => {
    setGoalScorers(goalScorers.filter((_, i) => i !== index));
  };

  const updateGoalScorer = (index, field, value) => {
    const updated = [...goalScorers];
    updated[index][field] = field === 'goals' ? parseInt(value) || 1 : value;
    setGoalScorers(updated);
  };

  const getPlayersForMatch = () => {
    if (!editingMatch) return [];
    return players.filter(p => 
      p.team_id === editingMatch.home_team_id || 
      p.team_id === editingMatch.away_team_id
    );
  };

  const getPlayersForTeam = (teamId) => {
    if (!teamId) return [];
    return players.filter(p => p.team_id === teamId);
  };

  const addMatchEvent = (eventType) => {
    const newEvent = eventType === 'goal'
      ? { event_type: 'goal', player_name: '', player_id: '', minute: '', team_id: '', assist_by: '', assist_player_id: '' }
      : (eventType === 'admin_yellow' || eventType === 'admin_red')
      ? { event_type: eventType, player_name: '', player_id: '', minute: '', team_id: '', admin_note: '' }
      : { event_type: eventType, player_name: '', player_id: '', minute: '', team_id: '' };
    setMatchEvents([newEvent, ...matchEvents]);
  };

  const updateMatchEvent = (index, field, value) => {
    const updated = [...matchEvents];
    updated[index][field] = value;
    setMatchEvents(updated);
    
    // Auto-calculate score from goals when events change
    if (editingMatch && (field === 'event_type' || field === 'team_id' || field === 'player_name')) {
      const homeGoals = updated.filter(e => e.event_type === 'goal' && e.team_id === editingMatch.home_team_id && (e.player_name || e.player_id)).length;
      const awayGoals = updated.filter(e => e.event_type === 'goal' && e.team_id === editingMatch.away_team_id && (e.player_name || e.player_id)).length;
      if (homeGoals > 0 || awayGoals > 0) {
        setScoreData(prev => ({ ...prev, home_score: homeGoals, away_score: awayGoals }));
      }
    }
  };

  const removeMatchEvent = (index) => {
    const updated = matchEvents.filter((_, i) => i !== index);
    setMatchEvents(updated);
    
    // Recalculate score after removing an event
    if (editingMatch) {
      const homeGoals = updated.filter(e => e.event_type === 'goal' && e.team_id === editingMatch.home_team_id && (e.player_name || e.player_id)).length;
      const awayGoals = updated.filter(e => e.event_type === 'goal' && e.team_id === editingMatch.away_team_id && (e.player_name || e.player_id)).length;
      setScoreData(prev => ({ ...prev, home_score: homeGoals, away_score: awayGoals }));
    }
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'goal': return <span className="text-sm">⚽</span>;
      case 'assist': return <span className="font-bold text-blue-400 text-xs">A</span>;
      case 'yellow_card': return <div className="w-3 h-4 bg-yellow-400 rounded-sm" />;
      case 'red_card': return <div className="w-3 h-4 bg-red-500 rounded-sm" />;
      case 'admin_card': return <div className="w-3 h-4 bg-orange-500 rounded-sm border border-orange-300" />;
      case 'admin_yellow': return <div className="w-3 h-4 bg-yellow-400 rounded-sm border-2 border-orange-500" />;
      case 'admin_red': return <div className="w-3 h-4 bg-red-500 rounded-sm border-2 border-orange-500" />;
      default: return null;
    }
  };

  const getEventLabel = (type) => {
    switch (type) {
      case 'goal': return 'Goal';
      case 'assist': return 'Assist';
      case 'yellow_card': return 'Yellow Card';
      case 'red_card': return 'Red Card';
      case 'admin_card': return 'Adm Card';
      case 'admin_yellow': return 'Adm Yellow';
      case 'admin_red': return 'Adm Red';
      default: return type;
    }
  };

  const teamMap = teams.reduce((acc, team) => {
    acc[team.team_id] = team;
    return acc;
  }, {});

  const stageOrder = ['pool_a', 'pool_b', 'pool_c', 'pool_d', 'group', 'quarterfinal', 'semifinal', 'gala', 'final'];

  const groupedMatches = matches.reduce((acc, match) => {
    const stage = match.stage || 'group';
    if (!acc[stage]) acc[stage] = [];
    acc[stage].push(match);
    return acc;
  }, {});
  
  // Sort matches within each stage by date and time (earliest first)
  Object.keys(groupedMatches).forEach(stage => {
    groupedMatches[stage].sort((a, b) => {
      const dateA = a.match_date || '';
      const dateB = b.match_date || '';
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      const timeA = a.match_time || '';
      const timeB = b.match_time || '';
      return timeA.localeCompare(timeB);
    });
  });

  return (
    <DashboardLayout>
      <div data-testid="matches-page" className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-black uppercase tracking-tight">
              {t('matches.title')}
            </h1>
            <p className="text-gray-600 mt-2">{t('matches.subtitle')} ({matches.length} {t('matches.total')})</p>
          </div>
          <div className="flex items-center gap-3">
            {organization && (
              <a
                href={`${API_URL}/api/calendar/${organization.org_id}.ics`}
                download
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-gray-100 border border-gray-200 text-gray-700 hover:bg-gray-200 rounded-lg text-sm transition-colors"
                data-testid="export-calendar-btn"
              >
                <Calendar size={14} /> Export Calendar
              </a>
            )}
            {matches.length === 0 && (
              <Button 
                onClick={handleAutoPopulate}
                disabled={autoPopulating}
                className="bg-orange-500 hover:bg-orange-600 text-black btn-lift"
                data-testid="auto-populate-btn"
              >
                {autoPopulating ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <Trophy size={18} className="mr-2" />
                )}
                {autoPopulating ? 'Creating...' : 'Auto-Populate Tournament (12 Teams, 4 Pools)'}
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-emerald-500 hover:bg-emerald-600 text-black btn-lift"
                  data-testid="add-match-btn"
                >
                  <Plus size={18} className="mr-2" />
                  {t('matches.addMatch')}
                </Button>
              </DialogTrigger>
            <DialogContent className="bg-white border-gray-200 text-black">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl uppercase">
                  {t('matches.scheduleNew')}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700">Home Team</Label>
                    <Select 
                      value={formData.home_team_id} 
                      onValueChange={(value) => setFormData({ ...formData, home_team_id: value })}
                    >
                      <SelectTrigger className="mt-2 bg-gray-100 border-gray-200 text-black">
                        <SelectValue placeholder="Select team" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {teams.map((team) => (
                          <SelectItem key={team.team_id} value={team.team_id} className="text-black hover:bg-gray-100">
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-700">Away Team</Label>
                    <Select 
                      value={formData.away_team_id} 
                      onValueChange={(value) => setFormData({ ...formData, away_team_id: value })}
                    >
                      <SelectTrigger className="mt-2 bg-gray-100 border-gray-200 text-black">
                        <SelectValue placeholder="Select team" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        {teams.map((team) => (
                          <SelectItem key={team.team_id} value={team.team_id} className="text-black hover:bg-gray-100">
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700">Date</Label>
                    <Input
                      type="date"
                      value={formData.match_date}
                      onChange={(e) => setFormData({ ...formData, match_date: e.target.value })}
                      required
                      data-testid="match-date-input"
                      className="mt-2 bg-gray-100 border-gray-200 text-black"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Time</Label>
                    <Input
                      type="time"
                      value={formData.match_time}
                      onChange={(e) => setFormData({ ...formData, match_time: e.target.value })}
                      required
                      data-testid="match-time-input"
                      className="mt-2 bg-gray-100 border-gray-200 text-black"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-700">Stage</Label>
                  <Select 
                    value={formData.stage} 
                    onValueChange={(value) => setFormData({ ...formData, stage: value })}
                  >
                    <SelectTrigger className="mt-2 bg-gray-100 border-gray-200 text-black">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {stages.map((stage) => (
                        <SelectItem key={stage} value={stage} className="text-black hover:bg-gray-100">
                          {stage.replace(/_/g, ' ').toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="flex-1 border-gray-200 text-black hover:bg-gray-100"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-black"
                    data-testid="match-submit-btn"
                  >
                    Schedule
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Score Update Dialog */}
        <Dialog open={scoreDialogOpen} onOpenChange={setScoreDialogOpen}>
          <DialogContent className="bg-white border-gray-200 text-black max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase">
                Update Score
              </DialogTitle>
            </DialogHeader>
            {editingMatch && (
              <form onSubmit={handleUpdateScore} className="flex flex-col flex-1 min-h-0 gap-4 mt-2">
                <div className="flex items-center justify-center gap-6 shrink-0">
                  <div className="text-center">
                    <p className="text-gray-600 text-sm mb-2">
                      {teamMap[editingMatch.home_team_id]?.name || 'Home'}
                    </p>
                    <Input
                      type="number"
                      min="0"
                      value={scoreData.home_score}
                      onChange={(e) => setScoreData({ ...scoreData, home_score: e.target.value })}
                      className="w-20 text-center text-2xl font-bold bg-gray-100 border-gray-200 text-black"
                      data-testid="home-score-input"
                    />
                  </div>
                  <span className="text-3xl text-gray-500 font-bold">-</span>
                  <div className="text-center">
                    <p className="text-gray-600 text-sm mb-2">
                      {teamMap[editingMatch.away_team_id]?.name || 'Away'}
                    </p>
                    <Input
                      type="number"
                      min="0"
                      value={scoreData.away_score}
                      onChange={(e) => setScoreData({ ...scoreData, away_score: e.target.value })}
                      className="w-20 text-center text-2xl font-bold bg-gray-100 border-gray-200 text-black"
                      data-testid="away-score-input"
                    />
                  </div>
                </div>

                {/* Scrollable middle section */}
                <div className="flex-1 overflow-y-auto min-h-0 space-y-4 pr-1">

                {/* Penalty Shootout (knockout matches only) */}
                {editingMatch && ['quarterfinal','semifinal','final'].includes(editingMatch.stage) && (
                  <div className="border border-amber-200 rounded-lg p-4 bg-amber-50">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={penalties}
                        onChange={(e) => setPenalties(e.target.checked)}
                        className="w-4 h-4 accent-amber-500"
                        data-testid="penalties-checkbox"
                      />
                      <span className="text-sm font-medium text-amber-800">Decided by Penalty Shootout</span>
                    </label>
                    {penalties && (
                      <div className="mt-3 flex items-center justify-center gap-4">
                        <div className="text-center">
                          <p className="text-amber-700 text-xs mb-1">{teamMap[editingMatch.home_team_id]?.name || 'Home'}</p>
                          <Input
                            type="number" min="0"
                            value={penScore.home}
                            onChange={(e) => setPenScore({ ...penScore, home: parseInt(e.target.value) || 0 })}
                            className="w-16 text-center text-lg font-bold bg-white border-amber-300 text-black"
                            data-testid="home-pen-score"
                          />
                        </div>
                        <span className="text-amber-600 font-bold text-lg">-</span>
                        <div className="text-center">
                          <p className="text-amber-700 text-xs mb-1">{teamMap[editingMatch.away_team_id]?.name || 'Away'}</p>
                          <Input
                            type="number" min="0"
                            value={penScore.away}
                            onChange={(e) => setPenScore({ ...penScore, away: parseInt(e.target.value) || 0 })}
                            className="w-16 text-center text-lg font-bold bg-white border-amber-300 text-black"
                            data-testid="away-pen-score"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Field & Time Editing */}
                <div className="grid grid-cols-3 gap-3 border-t border-gray-200 pt-4">
                  <div>
                    <Label className="text-gray-600 text-xs">Field</Label>
                    <Select
                      value={scoreData.field || "none"}
                      onValueChange={(value) => setScoreData({ ...scoreData, field: value === "none" ? "" : value })}
                    >
                      <SelectTrigger className="mt-1 bg-gray-100 border-gray-200 text-black text-sm h-9" data-testid="match-field-select">
                        <SelectValue placeholder="Select field" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="none" className="text-gray-600 text-xs">No field</SelectItem>
                        {['Field A', 'Field B', 'Field C', 'Field D', 'Field E'].map(f => (
                          <SelectItem key={f} value={f} className="text-black text-xs">{f}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-600 text-xs">Date</Label>
                    <Input
                      type="date"
                      value={scoreData.match_date}
                      onChange={(e) => setScoreData({ ...scoreData, match_date: e.target.value })}
                      className="mt-1 bg-gray-100 border-gray-200 text-black text-sm h-9"
                      data-testid="match-date-edit"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-600 text-xs">Time (MT)</Label>
                    <Input
                      type="time"
                      value={scoreData.match_time}
                      onChange={(e) => setScoreData({ ...scoreData, match_time: e.target.value })}
                      className="mt-1 bg-gray-100 border-gray-200 text-black text-sm h-9"
                      data-testid="match-time-edit"
                    />
                  </div>
                </div>

                {/* Match Events Section */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-gray-700 flex items-center gap-2">
                      <Clock size={16} className="text-emerald-500" />
                      Match Events
                    </Label>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Button
                      type="button"
                      onClick={() => addMatchEvent('goal')}
                      size="sm"
                      className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400"
                      data-testid="add-goal-event-btn"
                    >
                      <span className="mr-1">⚽</span> Goal
                    </Button>
                    <Button
                      type="button"
                      onClick={() => addMatchEvent('yellow_card')}
                      size="sm"
                      className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400"
                      data-testid="add-yellow-card-btn"
                    >
                      <div className="w-3 h-4 bg-yellow-400 rounded-sm mr-1" /> Yellow Card
                    </Button>
                    <Button
                      type="button"
                      onClick={() => addMatchEvent('red_card')}
                      size="sm"
                      className="bg-red-500/20 hover:bg-red-500/30 text-red-400"
                      data-testid="add-red-card-btn"
                    >
                      <div className="w-3 h-4 bg-red-500 rounded-sm mr-1" /> Red Card
                    </Button>
                    <Button
                      type="button"
                      onClick={() => addMatchEvent('admin_yellow')}
                      size="sm"
                      className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-orange-400/40"
                      data-testid="add-admin-yellow-btn"
                    >
                      <div className="w-3 h-4 bg-yellow-400 rounded-sm border-2 border-orange-500 mr-1" /> Adm YC
                    </Button>
                    <Button
                      type="button"
                      onClick={() => addMatchEvent('admin_red')}
                      size="sm"
                      className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-orange-400/40"
                      data-testid="add-admin-red-btn"
                    >
                      <div className="w-3 h-4 bg-red-500 rounded-sm border-2 border-orange-500 mr-1" /> Adm RC
                    </Button>
                  </div>

                  {matchEvents.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-3">
                      No match events added yet. Use the buttons above to add goals, assists, and cards.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                      {matchEvents.map((evt, index) => {
                        const needsPlayer = evt.event_type === 'goal' && evt.team_id && !evt.player_id && !evt.player_name;
                        return (
                        <div key={index} className={`rounded-lg p-2.5 ${needsPlayer ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-gray-100'}`} data-testid={`match-event-${index}`}>
                          {needsPlayer && <div className="text-amber-400 text-[10px] font-medium mb-1.5 uppercase tracking-wider">Assign scorer</div>}
                          {/* Row 1: Event type + Team select + Minute + Delete */}
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex items-center gap-1 min-w-[80px]">
                              {getEventIcon(evt.event_type)}
                              <span className="text-xs text-gray-600">{getEventLabel(evt.event_type)}</span>
                            </div>
                            <Select
                              value={evt.team_id || "none"}
                              onValueChange={(value) => {
                                const tid = value === "none" ? "" : value;
                                updateMatchEvent(index, 'team_id', tid);
                                updateMatchEvent(index, 'player_name', '');
                                updateMatchEvent(index, 'player_id', '');
                              }}
                            >
                              <SelectTrigger className="flex-1 bg-gray-100 border-gray-200 text-black text-xs h-8" data-testid={`event-team-${index}`}>
                                <SelectValue placeholder="Select team first" />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-gray-200">
                                <SelectItem value="none" className="text-gray-600 text-xs">Select team</SelectItem>
                                {editingMatch && (
                                  <>
                                    <SelectItem value={editingMatch.home_team_id} className="text-black text-xs">
                                      {teamMap[editingMatch.home_team_id]?.name || 'Home'}
                                    </SelectItem>
                                    <SelectItem value={editingMatch.away_team_id} className="text-black text-xs">
                                      {teamMap[editingMatch.away_team_id]?.name || 'Away'}
                                    </SelectItem>
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                            <Input
                              placeholder="Min"
                              value={evt.minute}
                              onChange={(e) => updateMatchEvent(index, 'minute', e.target.value)}
                              className="w-14 bg-gray-100 border-gray-200 text-black text-sm text-center h-8"
                              data-testid={`event-minute-${index}`}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeMatchEvent(index)}
                              className="text-gray-600 hover:text-red-500 p-1 h-8 w-8"
                            >
                              <X size={14} />
                            </Button>
                          </div>
                          {/* Row 2: Player dropdown (only when team is selected) */}
                          {(evt.event_type === 'admin_card' || evt.event_type === 'admin_yellow' || evt.event_type === 'admin_red') && evt.team_id ? (
                            <div className="ml-[88px] space-y-1.5">
                              <Input
                                placeholder="Person name (e.g. John Doe)"
                                value={evt.player_name}
                                onChange={(e) => updateMatchEvent(index, 'player_name', e.target.value)}
                                className="bg-gray-100 border-gray-200 text-black text-xs h-8"
                                data-testid={`event-admin-name-${index}`}
                              />
                              <Input
                                placeholder="Role (e.g. Team camera man, Coach, Supporter)"
                                value={evt.admin_note || ''}
                                onChange={(e) => updateMatchEvent(index, 'admin_note', e.target.value)}
                                className="bg-orange-50 border-orange-200 text-black text-xs h-8"
                                data-testid={`event-admin-note-${index}`}
                              />
                            </div>
                          ) : evt.team_id ? (
                            <div className="ml-[88px]">
                              <Select
                                value={evt.player_id || evt.player_name || "none"}
                                onValueChange={(value) => {
                                  if (value === "none") {
                                    updateMatchEvent(index, 'player_name', '');
                                    updateMatchEvent(index, 'player_id', '');
                                  } else {
                                    const p = getPlayersForTeam(evt.team_id).find(pl => pl.player_id === value);
                                    if (p) {
                                      updateMatchEvent(index, 'player_name', p.name);
                                      updateMatchEvent(index, 'player_id', p.player_id);
                                    }
                                  }
                                }}
                              >
                                <SelectTrigger className="w-full bg-gray-100 border-gray-200 text-black text-xs h-8" data-testid={`event-player-${index}`}>
                                  <SelectValue placeholder="Select player" />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-gray-200 max-h-48">
                                  <SelectItem value="none" className="text-gray-600 text-xs">Select player</SelectItem>
                                  {getPlayersForTeam(evt.team_id).sort((a, b) => (a.number || 99) - (b.number || 99)).map(p => (
                                    <SelectItem key={p.player_id} value={p.player_id} className="text-black text-xs">
                                      #{p.number} {p.name} ({p.position})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ) : (
                            <p className="text-gray-600 text-xs ml-[88px]">Select a team first to pick a player</p>
                          )}
                          {/* Row 3: Assist dropdown for goal events */}
                          {evt.event_type === 'goal' && evt.team_id && (
                            <div className="flex items-center gap-2 mt-1.5 ml-[88px]">
                              <span className="font-bold text-blue-400 text-xs">A</span>
                              <Select
                                value={evt.assist_player_id || evt.assist_by || "none"}
                                onValueChange={(value) => {
                                  if (value === "none") {
                                    updateMatchEvent(index, 'assist_by', '');
                                    updateMatchEvent(index, 'assist_player_id', '');
                                  } else {
                                    const p = getPlayersForTeam(evt.team_id).find(pl => pl.player_id === value);
                                    if (p) {
                                      updateMatchEvent(index, 'assist_by', p.name);
                                      updateMatchEvent(index, 'assist_player_id', p.player_id);
                                    }
                                  }
                                }}
                              >
                                <SelectTrigger className="flex-1 bg-gray-100 border-gray-200 text-blue-300 text-xs h-7" data-testid={`event-assist-${index}`}>
                                  <SelectValue placeholder="Assist by (optional)" />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-gray-200 max-h-48">
                                  <SelectItem value="none" className="text-gray-600 text-xs">No assist</SelectItem>
                                  {getPlayersForTeam(evt.team_id).filter(p => p.player_id !== evt.player_id).sort((a, b) => (a.number || 99) - (b.number || 99)).map(p => (
                                    <SelectItem key={p.player_id} value={p.player_id} className="text-black text-xs">
                                      #{p.number} {p.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Legacy Goal Scorers Section */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-gray-700 flex items-center gap-2">
                      <Target size={16} className="text-emerald-500" />
                      Goal Scorers (Stats)
                    </Label>
                    <Button
                      type="button"
                      onClick={addGoalScorer}
                      size="sm"
                      className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-500"
                      data-testid="add-goal-scorer-btn"
                    >
                      <Plus size={14} className="mr-1" />
                      Add Scorer
                    </Button>
                  </div>
                  
                  {goalScorers.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-3">
                      No goal scorers added yet
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {goalScorers.map((gs, index) => (
                        <div key={index} className="flex items-center gap-2 bg-gray-100 rounded-lg p-2">
                          <Select
                            value={gs.player_id}
                            onValueChange={(value) => updateGoalScorer(index, 'player_id', value)}
                          >
                            <SelectTrigger className="flex-1 bg-gray-100 border-gray-200 text-black text-sm">
                              <SelectValue placeholder="Select player" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                              {getPlayersForMatch().map((player) => (
                                <SelectItem 
                                  key={player.player_id} 
                                  value={player.player_id}
                                  className="text-black hover:bg-gray-100"
                                >
                                  {player.name} ({teamMap[player.team_id]?.name || 'Unknown'})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            min="1"
                            max="10"
                            value={gs.goals}
                            onChange={(e) => updateGoalScorer(index, 'goals', e.target.value)}
                            className="w-16 text-center bg-gray-100 border-gray-200 text-black text-sm"
                            data-testid={`goal-count-${index}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeGoalScorer(index)}
                            className="text-gray-600 hover:text-red-500 p-1"
                          >
                            <X size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                </div>{/* End scrollable section */}

                <div className="flex gap-3 shrink-0 pt-3 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setScoreDialogOpen(false)}
                    className="border-gray-200 text-black hover:bg-gray-100"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={async () => {
                      try {
                        const response = await fetch(`${API_URL}/api/matches/${editingMatch.match_id}`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                          body: JSON.stringify({
                            home_score: parseInt(scoreData.home_score),
                            away_score: parseInt(scoreData.away_score),
                            goal_scorers: goalScorers.filter(gs => gs.player_id),
                            match_events: matchEvents.filter(e => e.event_type),
                            field: scoreData.field || null,
                            match_time: scoreData.match_time || null,
                            match_date: scoreData.match_date || null,
                            penalties: penalties || false,
                            home_pen_score: penalties ? penScore.home : null,
                            away_pen_score: penalties ? penScore.away : null,
                          })
                        });
                        if (!response.ok) throw new Error('Failed');
                        toast.success('Score saved (match still in progress)');
                        setScoreDialogOpen(false); setEditingMatch(null);
                        setGoalScorers([]); setMatchEvents([]); fetchMatches();
                      } catch (error) { toast.error('Failed to save score'); }
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-black"
                    data-testid="score-save-btn"
                  >
                    <Pencil size={16} className="mr-2" />
                    Save Score
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-black"
                    data-testid="score-submit-btn"
                  >
                    <Check size={18} className="mr-2" />
                    Mark Complete
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Matches List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : matches.length === 0 ? (
          <Card className="bg-white border-gray-200">
            <CardContent className="p-12 text-center">
              <Calendar className="mx-auto text-gray-600 mb-4" size={48} />
              <h3 className="text-black text-lg font-medium mb-2">{t('matches.noMatches')}</h3>
              <p className="text-gray-500">{t('matches.noMatchesSub')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {stageOrder.map(stage => {
              const stageMatches = groupedMatches[stage];
              if (!stageMatches || stageMatches.length === 0) return null;
              return (
              <div key={stage}>
                <h2 className="font-heading text-xl text-gray-600 uppercase tracking-wider mb-4">
                  {stageLabels[stage] || stage.replace(/_/g, ' ').toUpperCase()}
                </h2>
                <div className="space-y-3">
                  {stageMatches.map((match) => (
                    <Card 
                      key={match.match_id}
                      className="bg-white border-gray-200 card-hover"
                      data-testid={`match-card-${match.match_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 flex-1">
                            <span className={`px-3 py-1 rounded text-xs font-medium uppercase ${
                              match.status === 'completed' ? 'badge-win' :
                              match.status === 'playing' ? 'bg-green-500/20 text-green-400 animate-pulse' :
                              match.status === 'delayed' ? 'bg-red-500/20 text-red-400' :
                              'badge-scheduled'
                            }`}>
                              {match.status === 'playing' ? 'LIVE' : match.status}
                            </span>
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                {(teamMap[match.home_team_id]?.logo || teamMap[match.home_team_id]?.logo_url) ? (
                                  <img 
                                    src={teamMap[match.home_team_id].logo || teamMap[match.home_team_id].logo_url} 
                                    alt="" 
                                    className="w-8 h-8 object-contain"
                                  />
                                ) : (
                                  <div className="w-8 h-8 bg-gray-100 rounded-full" />
                                )}
                                <span className="text-black font-medium">
                                  {match.home_team_name || 'TBD'}
                                </span>
                              </div>
                              
                              {(match.status === 'completed' || match.status === 'playing') ? (
                                <div className="text-center px-4">
                                  <span className={`font-mono text-xl font-bold ${match.status === 'playing' ? 'text-green-400' : 'text-black'}`}>
                                    {match.home_score} - {match.away_score}{match.forfeit ? '*' : ''}
                                  </span>
                                  {match.penalties && <div className="text-amber-600 text-[9px] font-medium">(Pen: {match.home_pen_score}-{match.away_pen_score})</div>}
                                  {match.forfeit && <div className="text-amber-400 text-[9px] font-medium">*{match.forfeit}</div>}
                                </div>
                              ) : (
                                <span className="text-gray-500 px-4">vs</span>
                              )}
                              
                              <div className="flex items-center gap-2">
                                <span className="text-black font-medium">
                                  {match.away_team_name || 'TBD'}
                                </span>
                                {(teamMap[match.away_team_id]?.logo || teamMap[match.away_team_id]?.logo_url) ? (
                                  <img 
                                    src={teamMap[match.away_team_id].logo || teamMap[match.away_team_id].logo_url} 
                                    alt="" 
                                    className="w-8 h-8 object-contain"
                                  />
                                ) : (
                                  <div className="w-8 h-8 bg-gray-100 rounded-full" />
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            {match.status === 'delayed' && match.delay_reason && (
                              <span className="text-xs text-red-400 bg-red-500/10 px-2 py-0.5 rounded">
                                {match.delay_reason}
                              </span>
                            )}
                            {canManageReferees && hasValidReferee(match) && (
                              <div className="flex items-center gap-1 text-xs text-emerald-400 flex-wrap">
                                <Shield size={12} />
                                {match.main_referee_id && <span title="Center Referee">{getRefereeNameById(match.main_referee_id)}</span>}
                                {match.assistant_referee_1_id && <span className="text-gray-500">| AR1: <span className="text-gray-700">{getRefereeNameById(match.assistant_referee_1_id)}</span></span>}
                                {match.assistant_referee_2_id && <span className="text-gray-500">| AR2: <span className="text-gray-700">{getRefereeNameById(match.assistant_referee_2_id)}</span></span>}
                                {match.fourth_official_id && <span className="text-gray-500">| 4th: <span className="text-gray-700">{getRefereeNameById(match.fourth_official_id)}</span></span>}
                                {match.match_delegate_id && <span className="text-gray-500">| Del: <span className="text-gray-700">{getRefereeNameById(match.match_delegate_id)}</span></span>}
                              </div>
                            )}
                            <span className="text-gray-500 text-sm font-mono">
                              {match.match_date} • {match.match_time} MT
                            </span>
                            {match.field && (
                              <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">{match.field}</span>
                            )}
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              match.start_mode === 'manual' ? 'bg-orange-500/20 text-orange-400' : 'bg-emerald-500/10 text-emerald-400'
                            }`} title="Click to toggle start mode">
                              {(match.start_mode || 'auto') === 'auto' ? 'Auto' : 'Manual'}
                            </span>
                            <div className="flex gap-1">
                              {/* REVERT button for playing/delayed matches → back to scheduled */}
                              {(match.status === 'playing' || match.status === 'delayed') && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={async () => {
                                    if (!confirm('Revert this match back to "Scheduled"?')) return;
                                    try {
                                      await fetch(`${API_URL}/api/matches/${match.match_id}`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                        body: JSON.stringify({ status: 'scheduled', delay_reason: '', home_score: 0, away_score: 0, match_events: [], goal_scorers: [] })
                                      });
                                      toast.success('Match reverted to scheduled');
                                      fetchMatches();
                                    } catch { toast.error('Failed'); }
                                  }}
                                  className="text-yellow-400 hover:bg-yellow-500/10 text-xs h-7 px-2"
                                  data-testid={`revert-scheduled-${match.match_id}`}
                                  title="Revert to Scheduled"
                                >
                                  <RotateCcw size={12} className="mr-1" /> Reset to Schedule
                                </Button>
                              )}
                              {/* START button for scheduled/delayed matches */}
                              {(match.status === 'scheduled' || match.status === 'delayed') && (
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      await fetch(`${API_URL}/api/matches/${match.match_id}`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                        body: JSON.stringify({ status: 'playing' })
                                      });
                                      toast.success('Match started!');
                                      fetchMatches();
                                    } catch { toast.error('Failed'); }
                                  }}
                                  className="bg-green-600 hover:bg-green-700 text-black text-xs h-7 px-2"
                                  data-testid={`start-match-${match.match_id}`}
                                >
                                  <Clock size={12} className="mr-1" /> Start
                                </Button>
                              )}
                              {/* DELAY button for scheduled matches on manual mode */}
                              {match.status === 'scheduled' && (match.start_mode || 'auto') === 'manual' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={async () => {
                                    const reason = prompt('Select delay reason:\n1. Weather\n2. Logistics\n3. Administration\n4. Emergency\n5. Team(s) unprepared\n6. Other');
                                    const reasons = ['Weather', 'Logistics', 'Administration', 'Emergency', 'Team(s) unprepared', 'Other'];
                                    const r = reasons[parseInt(reason) - 1] || reason || 'Other';
                                    try {
                                      await fetch(`${API_URL}/api/matches/${match.match_id}`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                        body: JSON.stringify({ status: 'delayed', delay_reason: r })
                                      });
                                      toast.success('Match marked as delayed');
                                      fetchMatches();
                                    } catch { toast.error('Failed'); }
                                  }}
                                  className="text-orange-400 hover:bg-orange-500/10 text-xs h-7 px-2"
                                  data-testid={`delay-match-${match.match_id}`}
                                >
                                  <AlertTriangle size={12} />
                                </Button>
                              )}
                              {/* Toggle auto/manual */}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={async () => {
                                  const newMode = (match.start_mode || 'auto') === 'auto' ? 'manual' : 'auto';
                                  try {
                                    await fetch(`${API_URL}/api/matches/${match.match_id}`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                      body: JSON.stringify({ start_mode: newMode })
                                    });
                                    toast.success(`Switched to ${newMode} start`);
                                    fetchMatches();
                                  } catch { toast.error('Failed'); }
                                }}
                                className="text-gray-600 hover:text-black text-xs h-7 px-1"
                                title={`Switch to ${(match.start_mode || 'auto') === 'auto' ? 'manual' : 'auto'} start`}
                              >
                                <Clock size={12} />
                              </Button>
                              {canManageReferees && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openRefereeDialog(match)}
                                  className={`${hasValidReferee(match) ? 'text-emerald-400' : 'text-gray-600'} hover:text-emerald-300 h-7 px-1`}
                                  data-testid={`assign-referee-${match.match_id}`}
                                  title="Assign Referees"
                                >
                                  <Shield size={14} />
                                </Button>
                              )}
                              {/* Score/Edit button */}
                              {(() => {
                                const unassigned = (match.match_events || []).filter(e => e.event_type === 'goal' && !e.player_id && (!e.player_name || e.player_name === 'Goal')).length;
                                return (
                                  <Button
                                    size="sm"
                                    onClick={() => openScoreDialog(match)}
                                    className={`relative ${match.status === 'completed' 
                                      ? "text-gray-600 hover:text-black h-7 px-2" 
                                      : "bg-emerald-500 hover:bg-emerald-600 text-black h-7 px-2"}`}
                                    variant={match.status === 'completed' ? 'ghost' : 'default'}
                                    data-testid={`update-score-${match.match_id}`}
                                  >
                                    <Pencil size={12} className="mr-1" />
                                    Edit
                                    {unassigned > 0 && <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-black text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{unassigned}</span>}
                                  </Button>
                                );
                              })()}
                              {/* Reverse completed back to playing */}
                              {match.status === 'completed' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={async () => {
                                    if (!confirm('Reverse this match to "Playing"? Standings will be adjusted.')) return;
                                    try {
                                      await fetch(`${API_URL}/api/matches/${match.match_id}`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                        body: JSON.stringify({ status: 'playing' })
                                      });
                                      toast.success('Match reversed to playing');
                                      fetchMatches();
                                    } catch { toast.error('Failed'); }
                                  }}
                                  className="text-yellow-400 hover:bg-yellow-500/10 h-7 px-1"
                                  title="Reverse to playing"
                                  data-testid={`reverse-match-${match.match_id}`}
                                >
                                  <X size={12} />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(match.match_id)}
                                className="text-gray-600 hover:text-red-500 h-7 px-1"
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        </div>
                        {/* Match Events Summary - Side by Side */}
                        {match.match_events && match.match_events.length > 0 && (() => {
                          const homeEvts = match.match_events.filter(e => e.team_id === match.home_team_id);
                          const awayEvts = match.match_events.filter(e => e.team_id === match.away_team_id);
                          const otherEvts = match.match_events.filter(e => e.team_id !== match.home_team_id && e.team_id !== match.away_team_id);
                          
                          const renderEvt = (evt, i, align) => {
                            const rev = align === 'right';
                            const isGeneric = !evt.player_id && (!evt.player_name || evt.player_name === 'Goal');
                            const displayName = isGeneric ? 'Unassigned' : (evt.player_name || 'Unknown');
                            if (evt.event_type === 'goal') return (
                              <div key={`g-${i}`} className={`flex items-center gap-1 text-xs ${rev ? 'flex-row-reverse' : ''}`}>
                                <span>⚽</span><span className={`font-medium ${isGeneric ? 'text-amber-400 italic' : 'text-black'}`}>{displayName}</span>
                                {evt.minute && <span className="text-gray-500">{evt.minute}&apos;</span>}
                                {evt.assist_by && <span className="text-blue-400">(A: {evt.assist_by})</span>}
                              </div>);
                            if (evt.event_type === 'yellow_card') return (
                              <div key={`y-${i}`} className={`flex items-center gap-1 text-xs ${rev ? 'flex-row-reverse' : ''}`}>
                                <div className="w-2.5 h-3.5 bg-yellow-400 rounded-sm" /><span className="text-gray-700">{displayName}</span>
                                {evt.minute && <span className="text-gray-500">{evt.minute}&apos;</span>}
                              </div>);
                            if (evt.event_type === 'red_card') return (
                              <div key={`r-${i}`} className={`flex items-center gap-1 text-xs ${rev ? 'flex-row-reverse' : ''}`}>
                                <div className="w-2.5 h-3.5 bg-red-500 rounded-sm" /><span className="text-gray-700">{displayName}</span>
                                {evt.minute && <span className="text-gray-500">{evt.minute}&apos;</span>}
                              </div>);
                            if (evt.event_type === 'assist') return (
                              <div key={`a-${i}`} className={`flex items-center gap-1 text-xs ${rev ? 'flex-row-reverse' : ''}`}>
                                <span className="font-bold text-blue-400">A</span><span className="text-blue-300">{displayName}</span>
                                {evt.minute && <span className="text-gray-500">{evt.minute}&apos;</span>}
                              </div>);
                            if (evt.event_type === 'admin_card') return (
                              <div key={`ac-${i}`} className={`flex items-center gap-1 text-xs ${rev ? 'flex-row-reverse' : ''}`}>
                                <div className="w-2.5 h-3.5 bg-orange-500 rounded-sm border border-orange-300" /><span className="text-gray-700">{evt.player_name || 'Admin'}</span>
                                {evt.admin_note && <span className="text-orange-400 text-[10px]">({evt.admin_note})</span>}
                                {evt.minute && <span className="text-gray-500">{evt.minute}&apos;</span>}
                              </div>);
                            if (evt.event_type === 'admin_yellow') return (
                              <div key={`ay-${i}`} className={`flex items-center gap-1 text-xs ${rev ? 'flex-row-reverse' : ''}`}>
                                <div className="w-2.5 h-3.5 bg-yellow-400 rounded-sm border-2 border-orange-500" /><span className="text-gray-700">{evt.player_name || 'Admin'}</span>
                                {evt.admin_note && <span className="text-orange-400 text-[10px]">({evt.admin_note})</span>}
                                {evt.minute && <span className="text-gray-500">{evt.minute}&apos;</span>}
                              </div>);
                            if (evt.event_type === 'admin_red') return (
                              <div key={`ar-${i}`} className={`flex items-center gap-1 text-xs ${rev ? 'flex-row-reverse' : ''}`}>
                                <div className="w-2.5 h-3.5 bg-red-500 rounded-sm border-2 border-orange-500" /><span className="text-gray-700">{evt.player_name || 'Admin'}</span>
                                {evt.admin_note && <span className="text-orange-400 text-[10px]">({evt.admin_note})</span>}
                                {evt.minute && <span className="text-gray-500">{evt.minute}&apos;</span>}
                              </div>);
                            return null;
                          };
                          
                          return (
                            <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`match-events-summary-${match.match_id}`}>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-0.5">{homeEvts.map((e, i) => renderEvt(e, i, 'left'))}</div>
                                <div className="space-y-0.5">{awayEvts.map((e, i) => renderEvt(e, i, 'right'))}</div>
                              </div>
                              {otherEvts.length > 0 && <div className="mt-1 space-y-0.5">{otherEvts.map((e, i) => renderEvt(e, i, 'left'))}</div>}
                            </div>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Referee Assignment Dialog */}
        <Dialog open={refereeDialogOpen} onOpenChange={setRefereeDialogOpen}>
          <DialogContent className="bg-[#1a1a1b] border-gray-200">
            <DialogHeader>
              <DialogTitle className="text-black font-heading uppercase flex items-center gap-2">
                <Shield className="text-emerald-400" size={20} />
                Assign Referees
              </DialogTitle>
            </DialogHeader>
            
            {selectedMatchForReferee && (
              <div className="mt-4 space-y-4">
                <div className="bg-gray-100 rounded-lg p-3 text-center">
                  <span className="text-black">
                    {teamMap[selectedMatchForReferee.home_team_id]?.name || 'Home'} vs {teamMap[selectedMatchForReferee.away_team_id]?.name || 'Away'}
                  </span>
                  <p className="text-gray-600 text-sm">
                    {selectedMatchForReferee.match_date} • {selectedMatchForReferee.match_time}
                  </p>
                </div>
                
                {referees.length === 0 ? (
                  <div className="text-center py-6">
                    <Shield className="mx-auto text-gray-500 mb-2" size={32} />
                    <p className="text-gray-600 mb-4">No referees available. Add referees first.</p>
                    <Button
                      onClick={() => {
                        setRefereeDialogOpen(false);
                        navigate('/referees');
                      }}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      Add Referees
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label className="text-gray-700">Center Referee</Label>
                        <Select 
                          value={refereeAssignment.main_referee_id || "none"}
                          onValueChange={(value) => setRefereeAssignment({
                            ...refereeAssignment, 
                            main_referee_id: value === "none" ? "" : value
                          })}
                        >
                          <SelectTrigger className="bg-gray-100 border-gray-200 text-black">
                            <SelectValue placeholder="Select referee" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a1b] border-gray-200">
                            <SelectItem value="none" className="text-gray-600">Not assigned</SelectItem>
                            {referees.map(ref => (
                              <SelectItem 
                                key={ref.referee_id} 
                                value={ref.referee_id}
                                className="text-black hover:bg-gray-100"
                              >
                                {ref.name} ({ref.certification_level})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-gray-700">1st Assistant</Label>
                        <Select 
                          value={refereeAssignment.assistant_referee_1_id || "none"}
                          onValueChange={(value) => setRefereeAssignment({
                            ...refereeAssignment, 
                            assistant_referee_1_id: value === "none" ? "" : value
                          })}
                        >
                          <SelectTrigger className="bg-gray-100 border-gray-200 text-black">
                            <SelectValue placeholder="Select referee" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a1b] border-gray-200">
                            <SelectItem value="none" className="text-gray-600">Not assigned</SelectItem>
                            {referees.map(ref => (
                              <SelectItem 
                                key={ref.referee_id} 
                                value={ref.referee_id}
                                className="text-black hover:bg-gray-100"
                              >
                                {ref.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-gray-700">2nd Assistant</Label>
                        <Select 
                          value={refereeAssignment.assistant_referee_2_id || "none"}
                          onValueChange={(value) => setRefereeAssignment({
                            ...refereeAssignment, 
                            assistant_referee_2_id: value === "none" ? "" : value
                          })}
                        >
                          <SelectTrigger className="bg-gray-100 border-gray-200 text-black">
                            <SelectValue placeholder="Select referee" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a1b] border-gray-200">
                            <SelectItem value="none" className="text-gray-600">Not assigned</SelectItem>
                            {referees.map(ref => (
                              <SelectItem 
                                key={ref.referee_id} 
                                value={ref.referee_id}
                                className="text-black hover:bg-gray-100"
                              >
                                {ref.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-gray-700">Fourth Official</Label>
                        <Select 
                          value={refereeAssignment.fourth_official_id || "none"}
                          onValueChange={(value) => setRefereeAssignment({
                            ...refereeAssignment, 
                            fourth_official_id: value === "none" ? "" : value
                          })}
                        >
                          <SelectTrigger className="bg-gray-100 border-gray-200 text-black">
                            <SelectValue placeholder="Select referee" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a1b] border-gray-200">
                            <SelectItem value="none" className="text-gray-600">Not assigned</SelectItem>
                            {referees.map(ref => (
                              <SelectItem 
                                key={ref.referee_id} 
                                value={ref.referee_id}
                                className="text-black hover:bg-gray-100"
                              >
                                {ref.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-gray-700">Match Delegate</Label>
                        <Select 
                          value={refereeAssignment.match_delegate_id || "none"}
                          onValueChange={(value) => setRefereeAssignment({
                            ...refereeAssignment, 
                            match_delegate_id: value === "none" ? "" : value
                          })}
                        >
                          <SelectTrigger className="bg-gray-100 border-gray-200 text-black">
                            <SelectValue placeholder="Select referee" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1a1a1b] border-gray-200">
                            <SelectItem value="none" className="text-gray-600">Not assigned</SelectItem>
                            {referees.map(ref => (
                              <SelectItem 
                                key={ref.referee_id} 
                                value={ref.referee_id}
                                className="text-black hover:bg-gray-100"
                              >
                                {ref.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <Button 
                        type="button" 
                        variant="ghost" 
                        onClick={() => setRefereeDialogOpen(false)}
                        className="flex-1 text-gray-600"
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleRefereeAssignment}
                        className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                        data-testid="save-referee-assignment-btn"
                      >
                        Save Assignments
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
